import React, { useLayoutEffect, useRef, useState } from 'react'
import { LinearProgress, makeStyles, Tooltip, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import classNames from 'classnames'
import type { CalendarEvent } from 'paintscout'
import { formatCurrency, getCrews } from '@paintscout/util/builder'
import { CREW_COLORS } from '@paintscout/util'
import LockIcon from '@material-ui/icons/Lock'
import ProductionEventPopper from '../ProductionEventPopper'
import type { CrewPalette } from '../util'
import { getCrewPalette, getProjectTypeLabel } from '../util'
import { useProductionEventPermissions } from 'shared/hooks'

const useStyles = makeStyles<Theme, CrewPalette>((theme) => ({
  root: {
    position: 'relative',
    backgroundColor: ({ surface }) => surface,
    borderRadius: theme.borderRadius.md,
    cursor: 'pointer',
    transition: 'background-color 0.1s ease-in-out',
    '@media (hover: hover) and (pointer: fine)': {
      '&:hover': {
        backgroundColor: ({ surfaceHover }) => surfaceHover
      }
    }
  },
  viewOnlyOverlay: {
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: 'inherit',
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 10px,
          transparent 10px,
          transparent 13px
        )
      `
    }
  },
  lockIcon: {
    width: 14,
    height: 14,
    color: ({ accent }) => accent,
    marginLeft: 4
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1.5, 1.5, 0.5, 1.5),
    overflow: 'hidden'
  },
  titleRow: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(1),
    minWidth: 0
  },
  measurer: {
    position: 'absolute',
    visibility: 'hidden',
    pointerEvents: 'none',
    left: 0,
    top: 0,
    whiteSpace: 'nowrap',
    overflow: 'visible',
    textOverflow: 'clip'
  },
  subtitleRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    minWidth: 0,
    flexWrap: 'nowrap',
    overflow: 'hidden'
  },
  title: {
    color: ({ accent }) => accent,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(14),
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flex: 1,
    minWidth: 0
  },
  crewBadge: {
    display: 'inline-block',
    maxWidth: '100%',
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.borderRadius.md,
    backgroundColor: ({ accentSoft }) => accentSoft,
    color: theme.palette.common.white,
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 400,
    lineHeight: 1.4,
    flexShrink: 1,
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    verticalAlign: 'baseline'
  },
  subtitleText: {
    color: ({ accentSoft }) => accentSoft,
    fontSize: theme.typography.pxToRem(12),
    lineHeight: 1.4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    flexShrink: 1,
    minWidth: 0
  },
  dotSeparator: {
    color: ({ accent }) => accent,
    fontWeight: 900,
    margin: theme.spacing(0, 0.25),
    fontSize: theme.typography.pxToRem(14),
    lineHeight: 1,
    flexShrink: 0
  },
  price: {
    color: ({ accent }) => accent,
    fontWeight: 600,
    fontSize: theme.typography.pxToRem(13),
    whiteSpace: 'nowrap'
  },
  footer: {
    padding: theme.spacing(0, 1.5, 0.5, 1.5)
  },
  progressRoot: {
    height: theme.typography.pxToRem(6),
    borderRadius: '8px',
    backgroundColor: ({ track }) => track
  },
  progressBar: {
    backgroundColor: ({ progressFill }) => progressFill,
    borderRadius: '8px',
    transition: 'none'
  }
}))

interface WeekEventProps {
  event: CalendarEvent
}

export function WeekEvent(props: WeekEventProps) {
  const { event } = props

  const { options } = useClientOptions()
  const crews = getCrews({ options, includeTrashed: true })
  const eventCrew = event.crew ? crews.find((crew) => crew._id === event.crew) : null
  const palette = getCrewPalette(eventCrew?.color || CREW_COLORS[0][0])
  const classes = useStyles(palette)
  const projectTypeLabel = getProjectTypeLabel(event.projectType, options)
  const crewLabel = eventCrew?.name || 'Unassigned'

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { permissionToViewDetails } = useProductionEventPermissions(event)
  const isViewOnly = !permissionToViewDetails

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : e.currentTarget)
  }

  const priceText = formatCurrency({ options, value: event.deal?.value })
  const hoursText = event.deal?.hours ? `${event.deal.hours}h` : ''
  const hasPrice = event.deal?.value != null

  const titleRowRef = useRef<HTMLDivElement>(null)
  const titleMeasurerRef = useRef<HTMLSpanElement>(null)
  const priceMeasurerRef = useRef<HTMLSpanElement>(null)
  const [showPrice, setShowPrice] = useState(true)

  useLayoutEffect(() => {
    const row = titleRowRef.current
    if (!row) return
    const evaluate = () => {
      const titleW = titleMeasurerRef.current?.offsetWidth ?? 0
      const priceW = priceMeasurerRef.current?.offsetWidth ?? 0
      if (priceW === 0) return
      const gap = parseFloat(getComputedStyle(row).columnGap || '0') || 0
      setShowPrice(titleW + priceW + gap <= row.clientWidth)
    }
    evaluate()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(evaluate)
    ro.observe(row)
    return () => ro.disconnect()
  }, [event?.deal?.name, priceText, isViewOnly, hasPrice])

  return (
    <>
      <div
        className={classNames(classes.root, { [classes.viewOnlyOverlay]: isViewOnly })}
        style={{ cursor: isViewOnly ? 'default' : undefined }}
        onClick={isViewOnly ? undefined : handleClick}
      >
        <div className={classes.content}>
          <div ref={titleRowRef} className={classes.titleRow}>
            {(() => {
              const titleNode = (
                <Typography className={classes.title}>
                  {event?.deal?.name}
                  {isViewOnly && (
                    <Tooltip content={`You don't have permission to access this job.`} hideIcon>
                      <LockIcon className={classes.lockIcon} />
                    </Tooltip>
                  )}
                </Typography>
              )
              return hasPrice && !showPrice ? (
                <Tooltip content={`${event?.deal?.name ?? ''} — ${priceText}`} hideIcon disableWrapper>
                  {titleNode}
                </Tooltip>
              ) : (
                titleNode
              )
            })()}
            {hasPrice && showPrice && <Typography className={classes.price}>{priceText}</Typography>}
            <span className={classNames(classes.title, classes.measurer)} ref={titleMeasurerRef} aria-hidden>
              {event?.deal?.name}
              {isViewOnly && <LockIcon className={classes.lockIcon} />}
            </span>
            {hasPrice && (
              <span className={classNames(classes.price, classes.measurer)} ref={priceMeasurerRef} aria-hidden>
                {priceText}
              </span>
            )}
          </div>
          <div className={classes.subtitleRow}>
            <Tooltip content={eventCrew?.name || null} hideIcon disableWrapper>
              <span className={classes.crewBadge}>{crewLabel}</span>
            </Tooltip>
            {projectTypeLabel && (
              <Typography className={classNames(classes.subtitleText)} component="span">
                {projectTypeLabel}
              </Typography>
            )}
            {hoursText && projectTypeLabel && <span className={classes.dotSeparator}>•</span>}
            {hoursText && (
              <Typography className={classNames(classes.subtitleText)} component="span">
                {hoursText}
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.footer}>
          <LinearProgress
            classes={{
              root: classes.progressRoot,
              barColorPrimary: classes.progressBar
            }}
            value={event.progress || 0}
            variant="determinate"
          />
        </div>
      </div>

      <ProductionEventPopper event={event} onClose={() => setAnchorEl(null)} anchorEl={anchorEl} />
    </>
  )
}
